// This API queries the proxy middleware in ASP to directly get Drupal data
// The generated types allow parsing of the responses,
// which will then be transformed to match the requirements of the components

import { Iterable, List, Map, OrderedMap, Set } from 'immutable'
import * as Moment from 'moment'
import { SelectedQuery, SelectedRelationQuery } from 'typescript-odata-client'
import { Action, Fun } from 'widgets-for-react'
import { ApiResult } from '../../Shared/api/apiTypes'
import { catchError } from '../../Shared/api/apiUtils'
import { getDrupalActivities } from '../../Shared/api/drupal/drupalApi'
import * as DrupalModel from '../../Shared/api/drupal/drupalTypes'
import { placeholderStyleUrls } from '../../Shared/api/drupal/drupalTypes'
import { ActivitySummaryProps } from '../../Shared/components/LegacyProps'
import { node_parser } from '../../Shared/api/drupal/parser'
import { Node } from '../../Shared/api/drupal/types'
import { lastClusterOfDigits } from '../../Shared/utils/string'
import { ActivityClusterProps } from '../custom_components/components/activity_cluster'
import { CookieUpdater } from '../custom_components/components/cookiebar'
import { Page } from '../custom_components/components/page_renderer'
import { PeriodSlug } from '../custom_components/components/region_and_date_filter'
import { OverviewVacancy } from '../custom_components/components/vacancy_overview_block'
import * as QueryContext from '../custom_components/odata_context'
import { OvaticLocation } from '../custom_components/pages/Location/LocationState'
import { ActivityEditionN, ActivityEvent } from '../custom_models/activity_edition'
import { Report } from '../custom_models/report'
import { moment } from '../custom_utils/moment'
import { Option } from '../custom_utils/option'
import { library } from '../libraries'
import * as ConvertNodeTo from './drupal_node_conversion'

const odataBaseUrl = `/api/v1/OData/GetQuery?Query=`

interface DatePeriod {
  start: Moment.Moment;
  end: Moment.Moment;
}

export type Region = {
  id: number
  slug: string
  name: string
  locations: List<Location>
}

export type Location = number

export const loadingText = "Bezig met laden...";
export const errorText = "De data kon niet worden geladen.";
export const noResultsText = "Er zijn geen resultaten gevonden.";

const drupalUrl = "/api/drupal";
const previewUrl = "/api";

const get: <A>(url: string, processor?: Action<A>) => Promise<ApiResult<A>> = <A>(url, processor = x => x) => {
  return fetch(url)
    .then(x => (
      x.status == 200 ? x.json() :
      x.status == 404 ? getNotFound<A>() :
      Promise.reject(x.statusText)))
    .then(processor)
    .then((x: A) => x as ApiResult<A>)
    .catch(catchError<A>());
};

const getNotFound: <A>() => Promise<ApiResult<A>> = <A>() => {
  return fetch(drupalUrl + library().notFoundPath)
    .then(x => (x.status == 200 ? x.json() : Promise.reject(x.statusText)))
    .then((x: A) => x as ApiResult<A>)
    .catch(catchError<A>());
};

export const getDatesForPeriod: (p: PeriodSlug) => DatePeriod = p => {
  let start = Moment().startOf('day')
  let end = Moment().endOf('day')

  start.add(p == 'morgen' ? 1 : 0, 'days')
  end.add(
    p == 'vandaag' ? 0
      : p == 'morgen' ? 1
        : p == 'komende-zeven-dagen' ? 7
          : p == 'komende-dertig-dagen' ? 30
            : 182,
    'days'
  )

  return { start, end }
};

const constructUrl = (route: string, params: string): string => {
  const preview = new RegExp("^/node-preview/").test(route);
  if (preview) {
    return `${previewUrl}${route}${params}`
  }
  return `${drupalUrl}${route}`
}

export const getPage: (route: string, params: string, cookieUpdater: CookieUpdater) => Promise<ApiResult<Page>> = (r, p, c) => {
  return get<Node>(constructUrl(r, p)).then(x => {
    if (x == "loading" || x == "error") return "error";

    try {
      const n = node_parser(x);

      const p =
          n.bundle == "activity_clusters"
        ? ConvertNodeTo.activityClustersPage(n, c)

        : n.bundle == "activity_detail"
        ? ConvertNodeTo.activityDetailPage(n, c)

        : n.bundle == "blog_overview"
        ? ConvertNodeTo.blogOverviewPage(n, c)

        : n.bundle == "blog_post"
        ? ConvertNodeTo.blogPostPage(n, c)

        : n.bundle == "collection_overview"
        ? ConvertNodeTo.collectionPage(n, c)

        : n.bundle == "location_detail"
        ? ConvertNodeTo.locationDetailPage(n, c)

        : n.bundle == "news_item"
        ? ConvertNodeTo.newsItemPage(n, c)

        : n.bundle == "news_overview"
        ? ConvertNodeTo.newsOverviewPage(n, c)

        : n.bundle == "newsletter"
        ? ConvertNodeTo.newsletterSignupPage(n, c)

        : n.bundle == "home"
        ? ConvertNodeTo.homePage(n, c)

        : n.bundle == "report_overview"
        ? ConvertNodeTo.reportOverview(n, c)

        : n.bundle == "location"
        ? ConvertNodeTo.locationPage(n, c)

        : n.bundle == "location_overview"
        ? ConvertNodeTo.locationOverviewPage(n, c)

        : n.bundle == "faq"
        ? null // This content type is not a page

        : n.bundle == 'event' // Events will be handled by the Events feature
        ? 'error'

        : ConvertNodeTo.generalContentPage(n, c);

      return p;
    } catch (e) {
      console.log(e);
      console.log("Are you sure all the required fields in Drupal are filled?");
    }

    return null;
  })
};

export const getRegions: () => Promise<ApiResult<Map<string, Region>>> = () => {
  let regions = QueryContext.RegionQuery(odataBaseUrl)
    .Select("Id", "Title", 'Slug')
    .IncludeStrict("Region_Location", q => q.Select("RegionId", "LocationId"))
    .ToList()

  return regions.then(r => {
    try {
      return r.reduce(
        (a, e) => a.set(
          e.Title,
          { 
            id: e.Id, 
            slug: e.Slug,
            name: e.Title,
            locations: e.Region_Location.map(l => l.LocationId).toList()
          }
        ),
        Map<string, Region>()
      )
        .sort((a, b) => a.slug.localeCompare(b.slug))
        .toOrderedMap() as ApiResult<Map<string, Region>>

    } catch (e) {
      console.log
      return "error" as ApiResult<any>
    }
  })
};

type SelectCondition<S> = (s: S) => S
const selectWhere = <Q>(q: Q, c: SelectCondition<Q>) => c(q)

type ActivityClusterPick = Pick<QueryContext.ActivityCluster, "Id" | "Title" | "Description" | "Slug">
export type ActivityClusterSelectedQuery = SelectedQuery<QueryContext.ActivityCluster, ActivityClusterPick>
export type ActivityClusterSelectedRelationQuery = SelectedRelationQuery<QueryContext.ActivityCluster, ActivityClusterPick>

type ActivityPick = Pick<QueryContext.Activity, "Id" | "Title" | "Sticky" | "HideInOverview" | "StickyTip">
export type ActivitySelectedQuery = SelectedQuery<QueryContext.Activity, ActivityPick>
export type ActivitySelectedRelationQuery = SelectedRelationQuery<QueryContext.Activity, ActivityPick>

type EditionPick = Pick<QueryContext.ActivityEditionN, "Id" | "SignupText" | "SignupURL">
export type EditionSelectedQuery = SelectedQuery<QueryContext.ActivityEditionN, EditionPick>
export type EditionSelectedRelationQuery = SelectedRelationQuery<QueryContext.ActivityEditionN, EditionPick>

type EventPick = Pick<QueryContext.ActivityEvent, "Id" | "Date" | "Start" | "End">
export type EventSelectedQuery = SelectedQuery<QueryContext.ActivityEvent, EventPick>
export type EventSelectedRelationQuery = SelectedRelationQuery<QueryContext.ActivityEvent, EventPick>

type LocationPick = Pick<QueryContext.Location, "Id" | "Title" | "Adres">
export type LocationSelectedQuery = SelectedQuery<QueryContext.Location, LocationPick>
export type LocationSelectedRelationQuery = SelectedRelationQuery<QueryContext.Location, LocationPick>

type RegionPick = Pick<QueryContext.Region, "Id" | "Title" | "Slug">
export type RegionSelectedQuery = SelectedQuery<QueryContext.Region, RegionPick>
export type RegionSelectedRelationQuery = SelectedRelationQuery<QueryContext.Region, RegionPick>

export type QueryConditions<
  C extends ActivityClusterSelectedQuery | ActivityClusterSelectedRelationQuery,
  A extends ActivitySelectedQuery | ActivitySelectedRelationQuery,
  E extends EditionSelectedQuery | EditionSelectedRelationQuery,
  R extends RegionSelectedQuery | RegionSelectedRelationQuery,
  L extends LocationSelectedQuery | LocationSelectedRelationQuery,
  V extends EventSelectedQuery | EventSelectedRelationQuery
> = {
  cluster: Option<SelectCondition<C>>,
  activity: Option<SelectCondition<A>>,
  edition: Option<SelectCondition<E>>,
  region: Option<SelectCondition<R>>,
  location: Option<SelectCondition<L>>,
  event: Option<SelectCondition<V>>
}

// During aggregation the earliest date found is kept, hence the high initial Date
const initialEventAggregation = { date: new Date('9999'), locations: Set<{name: string, address: string}>(), regions: Set<string>() }

export const getActivityClusters: (f: QueryConditions<ActivityClusterSelectedQuery, ActivitySelectedRelationQuery, EditionSelectedRelationQuery, RegionSelectedRelationQuery, LocationSelectedRelationQuery,
  EventSelectedRelationQuery>) => Promise<ApiResult<List<ActivityClusterProps>>> =
  f => {
    let q = selectWhere(
        QueryContext
          .ActivityClusterQuery(odataBaseUrl)
          .Select('Id', 'Title', 'Description', 'Slug'),
        c => f.cluster.kind == 'some' ? f.cluster.value(c) : c
      )
      .IncludeStrict('ActivityCluster_Activity', q => q
        .Select('ActivityId', 'ActivityClusterId')
        .IncludeStrict('Activity', q =>
          selectWhere<SelectedRelationQuery<QueryContext.Activity, ActivityPick>>(
            q.Select('Title', 'Id', 'Sticky', 'HideInOverview', 'StickyTip'),
            a => f.activity.kind == "some" ? f.activity.value(a) : a
          )
            .IncludeStrict('Activity_ActivityEditionN', q => q
              .Select('ActivityId', 'ActivityEditionNId')
              .IncludeStrict('ActivityEditionN', q =>
                selectWhere(
                  q.Select('Id', 'SignupText', 'SignupURL'),
                  e => f.edition.kind == 'some' ? f.edition.value(e) : e
                )
                  .IncludeStrict('ActivityEditionN_ActivityEvent', ae => ae
                    .Select('ActivityEditionNId', 'ActivityEventId')
                    .IncludeStrict('ActivityEvent', ev =>
                      selectWhere(
                        ev.Select('Id', 'Date', 'Start', 'End'),
                        e => f.event.kind == 'some' ? f.event.value(e) : e
                          )
                      .IncludeStrict('Location_ActivityEvent', q => q
                        .Select('LocationId', 'ActivityEventId')
                        .IncludeStrict('Location', q =>
                          selectWhere(
                            q.Select('Id', 'Title', 'Adres'),
                            q => f.location.kind == 'some' ? f.location.value(q) : q
                          )
                            .IncludeStrict('Region_Location', q => q
                              .Select('LocationId', 'RegionId')
                              .IncludeStrict('Region', q =>
                                selectWhere(
                                  q.Select('Id', 'Title', 'Slug'),
                                  r => f.region.kind == 'some' ? f.region.value(r) : r
                                )
                              )
                            )
                        )
                      )
                    )
                  )
              )
            )
        )
    ).ToList()

    try {
      let result = q.then(async data => {
        let activityIds = data.reduce(
          (l, c) => {
            c.ActivityCluster_Activity.map(a => l = l.push(a.ActivityId))
            return l
          },
          List<number>()
        )

        let drupalActivitiesData = await getDrupalActivities(activityIds)
        if (drupalActivitiesData == 'loading' || drupalActivitiesData == 'error' || drupalActivitiesData.length == 0) return List<ActivityClusterProps>()

        /** Map from pdb ID to Drupal Activity */
        const drupalActivities: Map<number, DrupalModel.Activity> = drupalActivitiesData.reduce(
          (acc, activity) => acc.set(Number(lastClusterOfDigits(activity.product_db_id)), activity),
          Map<number, DrupalModel.Activity>()
        )

        let props = data.reduce(
          (propsForClusters, cluster) => {
            // Define some types to help later on
            const activityPrototype = cluster.ActivityCluster_Activity.first().Activity.first()
            const eventPrototype = activityPrototype.Activity_ActivityEditionN.first().ActivityEditionN.first().ActivityEditionN_ActivityEvent.first().ActivityEvent.first()
            type QueriedActivity = typeof activityPrototype
            type QueriedEvent = typeof eventPrototype

            /** Aggregate all Events associated with a ActivityCluster_Activity row */
            const eventsPerActivity: (activity: QueriedActivity) => Iterable<number, QueriedEvent> = (activity) =>
              activity.Activity_ActivityEditionN.flatMap(a_ae => a_ae.ActivityEditionN.flatMap(ae => ae.ActivityEditionN_ActivityEvent.flatMap(ed_ev => ed_ev.ActivityEvent)))

            const initialClusterSplit = { stickyTips: List<QueriedActivity>(), stickies: List<QueriedActivity>(), regular: List<QueriedActivity>() }
            const activities = cluster.ActivityCluster_Activity.flatMap(ac_a => ac_a.Activity)

            // Split all Activities into Tips, Stickies and regular Activities
            const { stickyTips, stickies, regular } = activities.reduce((acc, activity) => {
              let { stickyTips, stickies, regular } = acc

              if (activity.StickyTip) {
                stickyTips = stickyTips.push(activity)
              } else if (activity.Sticky) {
                stickies = stickies.push(activity)
              } else {
                regular = regular.push(activity)
              }

              return { stickyTips, stickies, regular }
            }, initialClusterSplit)

            // Per list, sort all Activities by the earliest Event.Date associated with each Activity.
            // Then concatenate the resulting three sorted lists.
            const clusterActivities = List.of<Iterable<number, QueriedActivity>>(
              stickyTips.sortBy(activity => eventsPerActivity(activity).sortBy(event => event.Date).first().Date),
              stickies.sortBy(activity => eventsPerActivity(activity).sortBy(event => event.Date).first().Date),
              regular.sortBy(activity => eventsPerActivity(activity).sortBy(event => event.Date).first().Date)
            ).flatten()

            // TODO: Something is going wrong here.
            let activitySummaries: List<ActivitySummaryProps> = clusterActivities
              .reduce(
                (activityAccumulator, activity) => {
                  // Skip activity if no Drupal counterpart was found
                  if (!drupalActivities.has(activity.Id)) return activityAccumulator
                  const metadata = drupalActivities.get(activity.Id)

                  // Aggregate all Events associated with this row of the results
                  const allEvents = eventsPerActivity(activity)

                  // Given a list of events, grab the dates and the unique locations and regions from them
                  const { date, locations, regions } = allEvents.reduce((acc, _event) => {
                    const location = _event.Location_ActivityEvent.first().Location.first()
                    const region = location.Region_Location.first().Region.first()

                    return {
                      date: acc.date < _event.Date ? acc.date : _event.Date,
                      locations: acc.locations.add({name: location.Title || "", address: location.Adres || ""}),
                      regions: acc.regions.add(region.Title)
                    }
                  }, initialEventAggregation)

                  if (regions.isEmpty()) return activityAccumulator

                  return activityAccumulator.push({
                    headingLevel: 'h3',
                    title: metadata.title,
                    date: date,
                    location: locations.count() > 1 ? 'multiple' : locations.first()?.name || "",
                    address: locations.count() > 1 ? 'multiple' : locations.first()?.address || "",
                    subtitle: metadata.description,
                    route: metadata.url,
                    imageAlt: metadata.image_alt,
                    styleUrls: metadata.image?.field_media_image.image.style_urls || placeholderStyleUrls,
                    isTip: activity.StickyTip
                  })
                },
                List<ActivitySummaryProps>()
              )

            if (activitySummaries.isEmpty()) return propsForClusters

            return propsForClusters.push({
              title: cluster.Title,
              slug: cluster.Slug,
              description: cluster.Description,
              activitySummaries
            })
          },
          List<ActivityClusterProps>()
          ).sortBy(c => c.title.toLocaleLowerCase())

        return props
      })

      return result as ApiResult<Promise<List<ActivityClusterProps>>>
    } catch (e) {
      console.log(e)
      return "error" as ApiResult<any>
    }
  }

export const getActivityEditions: (f: QueryConditions<ActivityClusterSelectedRelationQuery, ActivitySelectedQuery, EditionSelectedRelationQuery, RegionSelectedRelationQuery, LocationSelectedRelationQuery,
  EventSelectedRelationQuery>) => Promise<ApiResult<List<ActivityEditionN>>> =
  f => {
    let q = selectWhere(
      QueryContext.ActivityQuery(odataBaseUrl).Select('Title', 'Id', 'Sticky', 'HideInOverview', 'StickyTip'),
      q => f.activity.kind == 'some' ? f.activity.value(q) : q
    ).IncludeStrict('Activity_ActivityEditionN', q => q
      .Select('ActivityId', 'ActivityEditionNId')
      .IncludeStrict('ActivityEditionN', q =>
        selectWhere(
          q.Select('Id', 'SignupText', 'SignupURL'),
          e => f.edition.kind == 'some' ? f.edition.value(e) : e
        )
          .Include('ActivityEditionN_Book', aeb => aeb
            .Select('ActivityEditionNId', 'BookId')
            .Include('Book', b => b.Select('Id', 'Name', 'Author', 'Image', 'URL'))
          )
          .IncludeStrict('ActivityEditionN_ActivityEvent', ae => ae
            .Select('ActivityEditionNId', 'ActivityEventId')
            .IncludeStrict('ActivityEvent', ev => selectWhere(
              ev.Select('Id', 'Date', 'Start', 'End'),
              e => f.event.kind == 'some' ? f.event.value(e) : e
            )
                .IncludeStrict('Location_ActivityEvent', q => q
              .Select('LocationId', 'ActivityEventId')
                .IncludeStrict('Location', q =>
                    selectWhere(
                      q.Select('Id', 'Title', 'Adres'),
                      q => f.location.kind == 'some' ? f.location.value(q) : q
                    )
                .IncludeStrict('Region_Location', q => q
                  .Select('LocationId', 'RegionId')
                   .IncludeStrict('Region', q =>
                      selectWhere(
                      q.Select('Id', 'Title', 'Slug'),
                      r => f.region.kind == 'some' ? f.region.value(r) : r
                  )
                )
              )
            )
           )
         )
       )
      )
    ).ToList()
    //console.log("getActivityEditions ", q)
    try {
      let result = q.then(
        async data => !data.isEmpty()
          ? data.first().Activity_ActivityEditionN.reduce(
            (l, a) => {
              let events: List<ActivityEvent> = List()

              a.ActivityEditionN.first().ActivityEditionN_ActivityEvent.forEach(x => {
                events = events.push({
                  id: x.ActivityEvent.first().Id,
                  date: x.ActivityEvent.first().Date,
                  start: x.ActivityEvent.first().Start,
                  end: x.ActivityEvent.first().End,
                  location: x.ActivityEvent.first().Location_ActivityEvent.first().Location.first().Title,
                  region: x.ActivityEvent.first().Location_ActivityEvent.first().Location.first().Region_Location.first().Region.first().Title,
                  address: x.ActivityEvent.first().Location_ActivityEvent.first().Location.first().Adres,
                })
              })

              if (events.isEmpty()) return l

              let activityBook = a.ActivityEditionN != null
                && a.ActivityEditionN.first() != null
                && a.ActivityEditionN.first().ActivityEditionN_Book != null
                && a.ActivityEditionN.first().ActivityEditionN_Book.first() != null
                && a.ActivityEditionN.first().ActivityEditionN_Book.first().Book != null?
                a.ActivityEditionN.first().ActivityEditionN_Book.first().Book.first() : null

              return l.push({
                id: a.ActivityEditionN.first().Id,
                signupText: a.ActivityEditionN.first().SignupText,
                signupUrl: a.ActivityEditionN.first().SignupURL,
                activityEvents: events,
                book: activityBook != null ? {
                  title: activityBook.Name,
                  author: activityBook.Author,
                  id: activityBook.Id,
                  url: activityBook.URL,
                  image: activityBook.Image,
                  imageLarge: ''
                } : null,
              })
            },
            List<ActivityEditionN>()
          )
        : List()
      )
      //console.log("getActivityEditions ", result)
      return result as ApiResult<Promise<List<ActivityEditionN>>>
    } catch (e) {
      console.log(e)
      return "error" as ApiResult<any>
    }
  }

export const getActivities: (f: QueryConditions<ActivityClusterSelectedRelationQuery, ActivitySelectedQuery, EditionSelectedRelationQuery, RegionSelectedRelationQuery, LocationSelectedRelationQuery,
  EventSelectedRelationQuery>) => Promise<ApiResult<List<ActivitySummaryProps>>> =
  f => {
    let q = selectWhere(
      QueryContext.ActivityQuery(odataBaseUrl).Select('Title', 'Id', 'Sticky', 'HideInOverview', 'StickyTip'),
      q => f.activity.kind == 'some' ? f.activity.value(q) : q
    ).IncludeStrict('Activity_ActivityEditionN', q => q
      .Select('ActivityId', 'ActivityEditionNId')
      .IncludeStrict('ActivityEditionN', q =>
        selectWhere(
          q.Select('Id', 'SignupText', 'SignupURL'),
          e => f.edition.kind == 'some' ? f.edition.value(e) : e
        )
          .IncludeStrict('ActivityEditionN_ActivityEvent', ae => ae
            .Select('ActivityEditionNId', 'ActivityEventId')
            .IncludeStrict('ActivityEvent', ev =>
              selectWhere(
                ev.Select('Id', 'Date', 'Start', 'End'),
                e => f.event.kind == 'some' ? f.event.value(e) : e
              )
              .IncludeStrict('Location_ActivityEvent', q => q
                .Select('LocationId', 'ActivityEventId')
                .IncludeStrict('Location', q =>
                  selectWhere(
                    q.Select('Id', 'Title', 'Adres'),
                    q => f.location.kind == 'some' ? f.location.value(q) : q
                  )
                    .IncludeStrict('Region_Location', q => q
                      .Select('LocationId', 'RegionId')
                      .IncludeStrict('Region', q =>
                        selectWhere(
                          q.Select('Id', 'Title', 'Slug'),
                          r => f.region.kind == 'some' ? f.region.value(r) : r
                        )
                      )
                    )
                )
              )
            )
          )
      )
    ).ToList()

    try {
      let result = q.then(async data => {
        let activityIds = data.reduce(
          (l, a) => l.push(a.Id),
          List<number>()
        )

        let drupalActivitiesData = await getDrupalActivities(activityIds)
        let drupalActivities = drupalActivitiesData != 'loading' && drupalActivitiesData != 'error'
          ? List<DrupalModel.Activity>(drupalActivitiesData)
          : List<DrupalModel.Activity>()

        // Sort activities by edition datetimes
        let activities = data
          .sort((a, b) => (
            a.Activity_ActivityEditionN.sort((c, d) => (
              c.ActivityEditionN.first().ActivityEditionN_ActivityEvent.first().ActivityEvent.first().Date.getTime()
              - d.ActivityEditionN.first().ActivityEditionN_ActivityEvent.first().ActivityEvent.first().Date.getTime()
            )).first().ActivityEditionN.first().ActivityEditionN_ActivityEvent.first().ActivityEvent.first().Date.getTime()
            -
            b.Activity_ActivityEditionN.sort((c, d) => (
              c.ActivityEditionN.first().ActivityEditionN_ActivityEvent.first().ActivityEvent.first().Date.getTime()
              - d.ActivityEditionN.first().ActivityEditionN_ActivityEvent.first().ActivityEvent.first().Date.getTime()
            )).first().ActivityEditionN.first().ActivityEditionN_ActivityEvent.first().ActivityEvent.first().Date.getTime()
          ))

        // Put StickyTip's in front, then Sticky's, then non sticky without loosing sorted order.
        activities = activities
          .filter(v => v.StickyTip)
          .concat(activities.filter(v => v.Sticky && !v.StickyTip))
          .concat(activities.filter(v => !v.Sticky && !v.StickyTip))

        return activities.reduce(
          (l, a) => {
            let dates = a.Activity_ActivityEditionN.reduce(
              (a, e) => a.push(e.ActivityEditionN.first().ActivityEditionN_ActivityEvent.first().ActivityEvent.first().Date),
              List<Date>()
            ).sort((l, r) => l.getTime() - r.getTime())

            let locations = a.Activity_ActivityEditionN.reduce(
              (a, e) => a.push(e.ActivityEditionN.first().ActivityEditionN_ActivityEvent.first().ActivityEvent.first().Location_ActivityEvent.first().Location.first().Title),
              List<string>()
            ).toSet()

            const metadata = drupalActivities
              .find(activity => Number(lastClusterOfDigits(activity.product_db_id)) == a.Id)
            if (!metadata) {
                console.warn(`Eligible activity ${a.Title} (${a.Id}) was not found in Drupal`)
                return l
              }
  
            return l.push({
              headingLevel: 'h4',
              title: metadata.title,
              subtitle: metadata.description,
              location: locations.count() > 1 ? 'multiple' : locations.first(),
              address: "",
              date: dates.count() > 1 ? 'multiple' : dates.count() == 1 ? dates.first() : 'multiple',
              imageAlt: metadata.image_alt,
              styleUrls: metadata.image?.field_media_image.image.style_urls || placeholderStyleUrls,
              route: metadata.url,
              isTip: a.StickyTip,
            })
          },
          List<ActivitySummaryProps>()
        )
      })

      return result as ApiResult<Promise<List<ActivitySummaryProps>>>
    } catch (e) {
      console.log(e)
      return "error" as ApiResult<any>
    }
  }

export const getNavigation: (
  id: string
) => Promise<ApiResult<DrupalModel.NavigationItem[]>> = id =>
  get(`${drupalUrl}/api/menu_items/${id}`);

export const getDrupalVacancies: (
  categoryId: List<number>
) => Promise<ApiResult<OverviewVacancy[]>> = categoryIds =>
  get(`${drupalUrl}/api/vacancies/${categoryIds.join("+")}`);

export const getDrupalLocations: (
  pdbIds: List<number>
) => Promise<ApiResult<DrupalModel.Location[]>> = pdbIds =>
  get(`${drupalUrl}/api/locations/${pdbIds.join("+")}`);

export const getDrupalReports: () => Promise<ApiResult<DrupalModel.Report[]>> = () =>
  get<DrupalModel.Report[]>(`${drupalUrl}/api/reports`)

export const getReports: () => Promise<ApiResult<Map<string, Report>>> = () =>
  new Promise<ApiResult<Map<string, Report>>>(async res => {
    let drupalReports = await getDrupalReports()
    let reports = Map<string, Report>()

    if (drupalReports != 'error' && drupalReports != 'loading') {
      drupalReports.forEach(r => {
        if (!reports.has(r.id)) {
          reports = reports.set(r.id, ({
            created: r.created,
            datetimes: Set(),
            imageAlt: r.imageAlt,
            imageUrl: r.imageUrl,
            imageUrlLarge: '',
            locations: Set(),
            shortDescription: r.shortDescription,
            title: r.title,
            url: r.url
          }))
        }

        reports = reports.set(r.id, ({
          ...reports.get(r.id),
          datetimes: r.datetime != "" ? reports.get(r.id).datetimes.add(moment.unix(parseInt(r.datetime))) : reports.get(r.id).datetimes,
          locations: r.location != "" ? reports.get(r.id).locations.add(r.location) : reports.get(r.id).locations
        }))
      })

      reports = reports.sort((a, b) => {
        if (a.datetimes.isEmpty() || b.datetimes.isEmpty()) return 0
        return b.datetimes.first().diff(a.datetimes.first())
      }).toOrderedMap()

      res(reports)
    } else {
      res('error')
    }
  })



export const getTeasers: Fun<string[], Promise<ApiResult<Map<string, DrupalModel.Teaser>>>> = (ids) => new Promise(async res => {
  try {
    let teasers = await get<DrupalModel.Teaser[]>(`${drupalUrl}/api/teasers/${ids.join("+")}`)
    
    if (teasers == 'error' || teasers == 'loading') throw new Error('Could not load teasers')

    res(
      OrderedMap<string, DrupalModel.Teaser>(teasers.map(t => [t.id, t]))
        .sort((a, b) => ids.indexOf(a.id) - ids.indexOf(b.id))
        .toOrderedMap()
    )
  } catch (e) {
    console.error(e)
    res('error')
  }  
})

const postProcessDrupalRoom = (room: DrupalModel.Room) => {
  const clean: DrupalModel.Room = {
    ...room,
    ovatic_id: Number(room.ovatic_id)
  }
  return clean
}

export const getDrupalOvaticLocations: () => Promise<ApiResult<DrupalModel.Room[]>> = () =>
  get<DrupalModel.Room[]>(
    `${drupalUrl}/api/rooms`,
    rooms => rooms.map(postProcessDrupalRoom)
  )

export const getOvaticLocations = async () => {
  try {
    let res = await fetch(`/api/v1/ovatic/GetAllLocations`)
    let json = await res.json()

    return json["locations"] as OvaticLocation[]
  } catch (e) {
    return catchError<OvaticLocation[]>()(e)
  }
}
